import React, { useMemo } from 'react';
import Pagination from 'rc-pagination';
import { shape, func, string, number, bool } from 'prop-types';

import Icon from 'components/icon';
import { getLocalization } from 'utils';

import { getFormattedPaginationLinkURL } from './utils';

const RCPagination = ({
    current,
    isLoading,
    localization,
    onChange,
    onLoadMoreCLick,
    pageSize,
    pageURLParameter = 'page',
    total,
    urlBase,
    withLoadMore,
    nextItemsAmount
}) => {
    const lastPage = useMemo(() => {
        if (!total || !pageSize) {
            return 0;
        }

        return Math.ceil(total / pageSize);
    }, [total, pageSize]);

    const isOnEnd = useMemo(() => current === lastPage, [current, lastPage]);

    const handleChange = nextPage => {
        if (onChange) {
            onChange(nextPage);
        }
    };

    const handlePageClick = event => {
        event.preventDefault();
    };

    const handleLoadMoreClick = event => {
        event.preventDefault();

        if (onLoadMoreCLick) {
            onLoadMoreCLick(current + 1);
        }
    };

    const itemRender = (page, type, element) => {
        switch (type) {
            case 'page':
                return (
                    <a
                        href={getFormattedPaginationLinkURL(
                            urlBase,
                            pageURLParameter,
                            page
                        )}
                        onClick={handlePageClick}
                    >
                        {page}
                    </a>
                );

            case 'jump-prev':
            case 'jump-next':
                return <span>&hellip;</span>;

            case 'prev':
                return (
                    <a
                        href={getFormattedPaginationLinkURL(
                            urlBase,
                            pageURLParameter,
                            page
                        )}
                        onClick={handlePageClick}
                        title={getLocalization(localization, 'previous')}
                    >
                        <Icon glyph="arrow-drop-left" />

                        <span className="u-sr-only">
                            {getLocalization(localization, 'previous')}
                        </span>
                    </a>
                );

            case 'next':
                return (
                    <a
                        href={getFormattedPaginationLinkURL(
                            urlBase,
                            pageURLParameter,
                            page
                        )}
                        onClick={handlePageClick}
                        title={getLocalization(localization, 'next')}
                    >
                        <Icon glyph="arrow-drop-right" />
                        <span className="u-sr-only">
                            {getLocalization(localization, 'next')}
                        </span>
                    </a>
                );

            default:
                return element;
        }
    };

    if (!current || !total || !pageSize) {
        return null;
    }

    return (
        <nav
            aria-label={getLocalization(localization, 'ariaLabel')}
            className="c-pagination"
        >
            <div className="c-pagination__col c-pagination__col--empty" />

            {withLoadMore && !isOnEnd && localization && localization.loadMore && (
                <div className="c-pagination__col c-pagination__col--button">
                    <a
                        href={getFormattedPaginationLinkURL(
                            urlBase,
                            pageURLParameter,
                            current + 1
                        )}
                        onClick={handleLoadMoreClick}
                        className={`button button--secondary loader-invert${
                            isLoading ? ' is-loading' : ''
                        }`}
                    >
                        {getLocalization(localization, 'loadMore')}{' '}
                        {nextItemsAmount ? `(${nextItemsAmount})` : null}
                    </a>
                </div>
            )}

            <div
                className={`c-pagination__col c-pagination__col--pagination ${
                    isLoading ? 'is-disabled' : ''
                }`}
            >
                <Pagination
                    current={current}
                    defaultPageSize={pageSize}
                    itemRender={itemRender}
                    onChange={handleChange}
                    showLessItems
                    showTitle={false}
                    total={total}
                />
            </div>
        </nav>
    );
};

RCPagination.propTypes = {
    urlBase: string.isRequired,
    current: number.isRequired,
    pageSize: number.isRequired,
    total: number.isRequired,
    onChange: func,
    localization: shape({
        ariaLabel: string,
        next: string,
        previous: string,
        loadMore: string
    }).isRequired,
    withLoadMore: bool,
    pageURLParameter: string,
    isLoading: bool,
    onLoadMoreCLick: func,
    nextItemsAmount: number
};

export default RCPagination;
